import * as _ from 'lodash';
import {Region} from '../../../../serp/classes';

/**
 * @typedef {object} RegionCityBrowserItem
 * @property {string} text
 * @property {Region[]} value
 */

export default [
  '$q',
  '$http',
  '$window',
  'RegionIds',
  'RegionUtilsService',
  ($q, $http, $window, RegionIds, RegionUtilsService) => {
    const service = {};

    /**
     * @memberOf RegionCityBrowserService
     */
    service.regionGroups = RegionIds.GROUPS.map(convertGroup);

    const topRegions = RegionUtilsService.getTopRegions();
    const regionsWithoutGroup = _.filter(topRegions, r => !findContainingGroup(r));

    /**
     * @type {?Region[]}
     */
    service.topRegions = topRegions;

    /**
     * @memberOf RegionCityBrowserService
     * @param {?Region[]} regions
     * @return {Promise}
     */
    service.getChildrenRegions = regions => {
      const def = $q.defer();
      resolveRegions(regions, def);
      return def.promise;
    };

    /**
     * @param {Region[]} regions
     * @param def
     */
    function resolveRegions(regions, def) {
      // Если родительский регион не указан, возвращаем все регионы России
      if (!regions) {
        def.resolve(regionsWithoutGroup);
        return;
      }

      // Регионы из группы отображаются обособленно, поэтому в основном списке пусто
      if (regions.length > 1) {
        def.resolve([]);
        return;
      }

      const region = regions[0];
      if (region.isCity()) {
        // Если город, то дочерних регионов нет
        def.resolve([]);
      } else {
        // Если это регион, то делаем запрос
        $http
          .get('/cian-api/site/v1/get-region-cities/?regionId=' + region.id, { cache: true })
          .then(response =>
            def.resolve(RegionUtilsService.convertToRegions(response.data.data.items)))
          .catch(() => def.resolve([]));
      }
    }

    /**
     * @memberOf RegionCityBrowserService
     * @param {string} query
     * @return {Region[]}
     */
    service.searchRegions = query => {
      const def = $q.defer();

      $http
        .get('/cian-api/site/v1/search-regions-cities/', { params: { text: query }, cache: true })
        .then(response =>
          def.resolve(RegionUtilsService.convertToRegions(response.data.data.items)))
        .catch(() => {
          console.error('API /search-regions-cities недоступно, ищем только по регионам страны');
          def.resolve(_.filter(
            topRegions, r => (new RegExp(query, 'i')).test(r.text)
          ));
        });

      return def.promise;
    };

    /**
     * @memberOf RegionCityBrowserService
     * @param {Region[]} regions
     * @return {{letter: string, regions: Regions[]}[]}
     */
    service.groupByFirstLetter = regions => {
      const unsortedList = _.transform(regions, (regionsByLetter, region) => {
        const letter = region.text[0];
        const letterGroup = _.find(regionsByLetter, { letter });
        if (letterGroup) {
          letterGroup.regions.push(region)
        } else {
          regionsByLetter.push({
            letter,
            regions: [region],
          })
        }
      }, []);

      return _.sortBy(unsortedList, 'letter');
    };

    /**
     * @memberOf RegionCityBrowserService
     * @param {Region[]} regions
     * @return {[{text: string, value: Region[]}]}
     */
    service.getRegionPath = regions => {
      const path = [{
        text: 'Россия',
        value: null,
      }];

      if (regions) {
        if (regions.length > 1) {
          // Группа регионов
          addToPath(path, findGroup(regions));
        } else {
          const region = regions[0];

          // Узнаем является ли городом переданный регион
          if (region.isCity()) {
            /** @type Region */
            const parentRegion = _.find(topRegions, { id: region.parentId });
            if (parentRegion) {
              // Группа, в которую входит родительский регион (если она есть)
              addToPath(path, findContainingGroup(parentRegion));
              // Родительский регион
              path.push({
                text: parentRegion.text,
                value: [parentRegion],
              });
            }
          } else {
            // Группа, в которую входит регион (если она есть)
            addToPath(path, findContainingGroup(region));

            // id=1 и id=2 указатели для Москвы и Санкт-Петербурга (тоже считаем за города)
            // нужно это т.к у них isCity() отдает false из-за отсутствия id родителя
            // поэтому не добавляем эти города в хлебные крошки
            if (region.id !== RegionIds.REGIONS.MOSCOW && region.id !== RegionIds.REGIONS.SPB) {
              path.push({
                text: region.text,
                value: [region],
              });
            }
          }
        }
      }
      return path;
    };

    /**
     * Метод нахождения группы присовенный сервису
     */
    service.findContainingGroup = findContainingGroup;

    // Не добавляет объект, если он falsy
    function addToPath(path, obj) {
      if (obj) path.push(obj);
    }

    /**
     * Находит группу, соответствующую переданным регионам
     * @memberOf RegionCityBrowserService
     * @param {Region[]} regions
     * @return {?RegionCityBrowserItem}
     */
    function findGroup(regions) {
      return convertGroup(RegionUtilsService.findGroup(regions));
    }

    /**
     * Находит группу, в которую входит регион
     * @param {Region} region
     * @return {?RegionCityBrowserItem}
     */
    function findContainingGroup(region) {
      return convertGroup(RegionUtilsService.findContainingGroup(region));
    }

    /**
     * @param {RegionGroup} group
     * @return {RegionCityBrowserItem}
     */
    function convertGroup(group) {
      if (group) return {
        text: group.shortName,
        value: group.regionIds.map(RegionUtilsService.getTopRegion),
      };
    }

    return service;
  }
];



// WEBPACK FOOTER //
// ./src/components/region-city-browser/src/scripts/cian.region-city-browser.service.js